/* =====================================================================================
   Step-up: enrol a second factor, and present it.

   One component covering both, because a person who has never enrolled and a person who has
   are in the same place at the same moment — about to open Notebook or Locker — and sending
   them to a settings screen first is how a security control becomes something people avoid.
   ===================================================================================== */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  assurance, beginEnrol, confirmEnrol, stepUp, listFactors, removeFactor, renderQr,
  type Enrolment, type Factor,
} from '../lib/mfa';
import { useDialog } from '../lib/ui';
import './stepup.css';
import { onFail } from '../lib/errors';

export function StepUpDialog({ appName, reason, onVerified, onClose }: {
  appName: string; reason?: string | null;
  onVerified: () => void; onClose: () => void;
}) {
  const ref = useDialog(onClose);
  const [mode, setMode] = useState<'checking' | 'enrol' | 'verify'>('checking');
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState<string | null>(null);

  // Draw the QR from the enrolment URI. Falls back to whatever the server sent, repaired with
  // a viewBox, if the generator cannot be loaded.
  useEffect(() => {
    let live = true;
    if (!enrolment?.uri) { setQr(null); return; }
    renderQr(enrolment.uri).then((svg) => { if (live) setQr(svg ?? enrolment.qrSvg ?? null); });
    return () => { live = false; };
  }, [enrolment]);

  useEffect(() => {
    assurance()
      .then((a) => setMode(a.enrolled ? 'verify' : 'enrol'))
      // Not an error worth surfacing: an account with no factor legitimately cannot report
      // its assurance level, and the right response is to offer enrolment.
      .catch(() => setMode('enrol'));
  }, []);

  async function startEnrol() {
    setBusy(true); setErr(null);
    try { setEnrolment(await beginEnrol(`${navigator.platform || 'Device'}`)); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function submit() {
    setBusy(true); setErr(null);
    try {
      if (mode === 'enrol' && enrolment) {
        await confirmEnrol(enrolment.factorId, code);
      } else {
        await stepUp(code);
      }
      onVerified();
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      setErr(m === 'no-factor'
        ? 'No authenticator is set up on this account yet. Set one up below.'
        : m);
      if (m === 'no-factor') setMode('enrol');
    } finally { setBusy(false); }
  }

  /* Rendered into document.body. The suite launcher sets z-index 161 and therefore creates a
     stacking context; a child cannot paint above its own ancestor however high its z-index,
     which is why this dialog appeared BEHIND the launcher. A portal is the fix, not a bigger
     number. */
  return createPortal(
    <>
      <div className="su-scrim" onClick={onClose} />
      <div className="su" role="dialog" aria-modal="true" aria-label={`Confirm it is you to open ${appName}`}
           ref={ref} onClick={(e) => e.stopPropagation()}>
        <h2 className="su-title">Confirm it is you to open {appName}</h2>

        {reason && <p className="su-reason">{reason}</p>}

        {mode === 'checking' && <p className="su-hint">Checking your account…</p>}

        {/* ---------- enrolment ---------- */}
        {mode === 'enrol' && (
          <>
            <p className="su-hint">
              This account has no second factor yet. Set one up once and it protects Notebook
              and Locker from here on.
            </p>

            {!enrolment ? (
              <div className="su-actions">
                <button className="btn-cancel" onClick={onClose} disabled={busy}>Back</button>
                <button className="btn-save" onClick={startEnrol} disabled={busy}>
                  {busy ? 'Preparing' : 'Set up an authenticator'}
                </button>
              </div>
            ) : (
              <>
                <p className="su-hint">
                  Scan this with an authenticator app such as 1Password, Authy, Google Authenticator,
                  or the built-in one on iOS. Then type the six digits it shows.
                </p>
                <div className="su-qr">
                  {qr
                    ? (
                      /* Generated locally from the otpauth URI. The server's own SVG carries no
                         viewBox, so CSS sizing clips it instead of scaling it, which is what
                         produced a QR that displayed correctly and scanned in nothing. */
                      <div className="su-qrbox" role="img" aria-label="Authenticator QR code"
                           dangerouslySetInnerHTML={{ __html: qr }} />
                    )
                    : <div className="su-qrbox su-qrwait">Preparing the code</div>}
                </div>

                <p className="su-secret">
                  <button className="link-btn" onClick={() => setShowSecret(!showSecret)}>
                    {showSecret ? 'Hide the key' : 'Cannot scan it? Type the key instead'}
                  </button>
                </p>
                {showSecret && enrolment.secret && (
                  <div className="su-key">
                    {/* A read-only input rather than a <code> block. A selection inside an input
                        cannot pick up neighbouring markup, so what reaches the clipboard is the
                        secret and nothing else. Selecting a <code> sitting in a flex row can
                        quietly take the button label with it, and one stray character in a
                        base32 key has it rejected outright. */}
                    <input className="su-keyfield" readOnly value={enrolment.secret}
                           aria-label="Setup key" spellCheck={false} autoComplete="off"
                           onFocus={(e) => e.currentTarget.select()}
                           onClick={(e) => e.currentTarget.select()} />
                    <button className="btn-compose"
                            onClick={() => navigator.clipboard?.writeText(enrolment.secret ?? '')
                              .then(() => setCopied(true))
                              // A refused clipboard is visible already: the label stays
                              // "Copy" and the key is on screen to select by hand.
                              .catch(() => setCopied(false))}>
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                    <span className="su-keyhint">
                      In Google Authenticator choose <strong>Enter a setup key</strong>, put this in
                      the <strong>Key</strong> field and leave the type as <strong>Time based</strong>.
                      A base32 key is upper case throughout and the only digits it can contain are
                      2 to 7, so there is no letter O, no zero and no one to confuse.
                    </span>
                  </div>
                )}
                <CodeEntry code={code} setCode={setCode} onSubmit={submit} busy={busy} />
                {err && <p className="su-err" role="alert">{err}</p>}
                <div className="su-actions">
                  <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
                  <button className="btn-save" onClick={submit} disabled={busy || code.length < 6}>
                    {busy ? 'Confirming' : 'Confirm and open'}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* ---------- verification ---------- */}
        {mode === 'verify' && (
          <>
            <p className="su-hint">
              Enter the current six-digit code from your authenticator. It changes every 30
              seconds. Verifying raises this session for 15 minutes, then you are asked again.
            </p>
            <CodeEntry code={code} setCode={setCode} onSubmit={submit} busy={busy} />
            {err && <p className="su-err" role="alert">{err}</p>}
            <div className="su-actions">
              <button className="btn-cancel" onClick={onClose} disabled={busy}>Back</button>
              <button className="btn-save" onClick={submit} disabled={busy || code.length < 6}>
                {busy ? 'Verifying' : 'Verify and open'}
              </button>
            </div>
          </>
        )}

        <p className="su-foot">
          The code is checked by the server, not by this page, and the result is written into
          your session token. Closing this window without verifying leaves it unchanged.
        </p>
      </div>
    </>,
    document.body,
  );
}

function CodeEntry({ code, setCode, onSubmit, busy }: {
  code: string; setCode: (v: string) => void; onSubmit: () => void; busy: boolean;
}) {
  return (
    <input
      className="su-code"
      inputMode="numeric"
      autoComplete="one-time-code"
      /* one-time-code lets iOS and macOS offer the code from Messages or the built-in
         authenticator, which removes the retyping that makes people disable MFA. */
      placeholder="000000"
      maxLength={6}
      value={code}
      disabled={busy}
      autoFocus
      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
      onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) onSubmit(); }}
    />
  );
}

/** Settings panel: what is enrolled, and how to remove it. */
export function MfaSettings() {
  const [factors, setFactors] = useState<Factor[]>([]);
  const [enrolling, setEnrolling] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => { listFactors().then(setFactors).catch(onFail('Your security keys', () => setFactors([]))); }, [nonce]);
  const verified = factors.filter((f) => f.status === 'verified');

  return (
    <section className="site-block">
      <h2>Two-factor authentication</h2>
      <p className="block-lead">
        Required to open Notebook and Locker. Those hold experimental evidence and identifiable
        patient records, and a signed-in laptop left on a desk should not be enough to reach them.
      </p>
      {err && <p className="block-error" role="alert">{err}</p>}

      {verified.length === 0 ? (
        <p className="att-empty">
          No authenticator set up. You will be asked to add one the first time you open
          Notebook or Locker.
        </p>
      ) : (
        <ul className="su-factors">
          {verified.map((f) => (
            <li key={f.id}>
              <strong>{f.friendly_name || 'Authenticator'}</strong>
              <span className="pill ok">verified</span>
              <button className="link-btn"
                      onClick={() => removeFactor(f.id).then(() => setNonce((n) => n + 1))
                        .catch((e) => setErr(String(e.message ?? e)))}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <button className="btn-compose" onClick={() => setEnrolling(true)}>Add an authenticator</button>

      {enrolling && (
        <StepUpDialog appName="your account"
                      reason="Adding a second factor. Scan the code and confirm to finish."
                      onClose={() => { setEnrolling(false); setNonce((n) => n + 1); }}
                      onVerified={() => { setEnrolling(false); setNonce((n) => n + 1); }} />
      )}
    </section>
  );
}
